import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  FaCalendarAlt,
  FaCommentDots,
  FaDice,
  FaMusic,
  FaPaperPlane,
  FaSearch,
  FaUser,
} from "react-icons/fa";
import Reveal from "../components/Reveal";
import ParanoidiButton from "../../admin/components/ui/ParanoidiButton";
import { demoSongs, quickSearches } from "../data/songs";
import {
  createSongRequest,
  createSongRequestBoostCheckout,
  getPublicPerformerEvents,
  getSongRequests,
  getSongs,
} from "../data/songRequestsApi";
import {
  getParanoidiSettings,
  subscribeParanoidiSettings,
} from "../data/paranoidiSettings";
import { trackEvent as trackAnalyticsEvent } from "../../lib/analytics";

const ReactConfetti = lazy(() => import("react-confetti"));

function fetchParanoidiSettings() {
  return Promise.resolve(getParanoidiSettings());
}

function getPublicApiHealthUrl() {
  if (typeof window === "undefined") {
    return "";
  }

  const hostname = String(window.location.hostname || "")
    .trim()
    .toLowerCase();

  const isLocalhost =
    hostname === "localhost" ||
    hostname === "127.0.0.1";

  return isLocalhost
    ? "http://localhost:4000/api/health"
    : "/backend/api/health";
}

async function warmPublicApiConnection() {
  const healthUrl = getPublicApiHealthUrl();

  if (!healthUrl) {
    return;
  }

  try {
    await fetch(healthUrl, {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
    });
  } catch {
    // Warm-up on vain suorituskykyoptimointi. Sen epäonnistuminen ei saa
    // estää biisitoiveen lähettämistä tai näyttää käyttäjälle virhettä.
  }
}

function isProductionAnalyticsHost() {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = String(window.location.hostname || "")
    .trim()
    .toLowerCase();

  return hostname === "paranoidi.fi" || hostname === "www.paranoidi.fi";
}

function trackEvent(eventName, params = {}) {
  // Julkisen biisitoivepolun uudet eventit lähetetään vain tuotannossa.
  // Näin localhost ja staging eivät sotke paranoidi.fi:n GA4-dataa.
  if (!isProductionAnalyticsHost()) {
    return;
  }

  trackAnalyticsEvent(eventName, params);
}

function trackSongRequest(title, artist, params = {}) {
  trackEvent("song_request_submitted", {
    song_title: String(title || "").trim(),
    artist: String(artist || "").trim(),
    ...params,
  });
}

function resolveSponsorAssetUrl(url) {
  const rawUrl = String(url || "").trim();

  if (!rawUrl) {
    return "";
  }

  if (
    rawUrl.startsWith("http://") ||
    rawUrl.startsWith("https://") ||
    rawUrl.startsWith("data:") ||
    rawUrl.startsWith("blob:")
  ) {
    return rawUrl;
  }

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  if (isLocalhost && rawUrl.startsWith("/backend/")) {
    return `http://localhost:4000${rawUrl}`;
  }

  if (isLocalhost && rawUrl.startsWith("/uploads/")) {
    return `http://localhost:4000${rawUrl}`;
  }

  return rawUrl;
}


function normalize(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\b(90|80|70|60|50)\s*-?\s*luku\b/g, "$1luku")
    .replace(/\b(1990|1980|1970|1960|1950)\s*-?\s*luku\b/g, "$1luku")
    .replace(/[-\s]+/g, "");
}

function getDecade(year) {
  if (!year) {
    return "";
  }

  return `${Math.floor(year / 10) * 10}-luku`;
}

function getSongDecade(song) {
  return song?.decade || getDecade(song?.year);
}

function getSongIsRequestable(song) {
  const rawValue = song?.isRequestable ?? song?.is_requestable;

  if (rawValue === undefined || rawValue === null) {
    return true;
  }

  if (typeof rawValue === "boolean") {
    return rawValue;
  }

  if (typeof rawValue === "number") {
    return rawValue === 1;
  }

  return String(rawValue).toLowerCase() !== "false" && String(rawValue) !== "0";
}

function getSongLanguage(song) {
  return song?.language || "unknown";
}

function isSongAllowedByLanguageMode(song, languageMode) {
  const normalizedLanguageMode = String(languageMode || "all")
    .trim()
    .toLowerCase();

  if (normalizedLanguageMode === "all") {
    return true;
  }

  return getSongLanguage(song) === normalizedLanguageMode;
}

function normalizeSongFromApi(song) {
  const mood = Array.isArray(song.mood)
    ? song.mood
    : String(song.mood || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

  return {
    ...song,
    title: song.title || "",
    artist: song.artist || "",
    year: song.year || null,
    decade: song.decade || getDecade(song.year),
    genre: song.genre || "",
    language: getSongLanguage(song),
    mood,
    tags: song.tags || [],
    isRequestable: getSongIsRequestable(song),
  };
}

function getSongSearchText(song) {
  const decade = getSongDecade(song);

  return normalize(
    [
      song.title,
      song.artist,
      song.year,
      decade,
      song.genre,
    ].join(" ")
  );
}

function scoreSong(song, rawSearch) {
  const search = normalize(rawSearch.trim());

  if (!search) {
    return 0;
  }

  const title = normalize(song.title);
  const artist = normalize(song.artist);
  const decade = normalize(getSongDecade(song));
  const haystack = getSongSearchText(song);
  const terms = search.split(/\s+/).filter(Boolean);

  let score = 0;

  if (title === search) {
    score += 100;
  }

  if (artist === search) {
    score += 80;
  }

  if (title.startsWith(search)) {
    score += 60;
  }

  if (artist.startsWith(search)) {
    score += 45;
  }

  if (title.includes(search)) {
    score += 35;
  }

  if (artist.includes(search)) {
    score += 25;
  }

  if (decade.includes(search)) {
    score += 20;
  }

  if (haystack.includes(search)) {
    score += 10;
  }

  for (const term of terms) {
    if (title.includes(term)) {
      score += 20;
    }

    if (artist.includes(term)) {
      score += 16;
    }

    if (haystack.includes(term)) {
      score += 8;
    }
  }

  const allTermsFound = terms.every((term) => haystack.includes(term));

  if (!allTermsFound && score < 35) {
    return 0;
  }

  return score;
}

const fallbackRequestSponsors = [];



const RANDOM_DAILY_LIMIT = 10;
const RANDOM_LIMIT_STORAGE_KEY = "paranoidi_random_song_daily_limit";

function getTodayKey() {
  return new Date().toISOString().slice(0, 10);
}

function getRandomLimitState() {
  if (typeof window === "undefined") {
    return {
      date: getTodayKey(),
      count: 0,
    };
  }

  try {
    const raw = window.localStorage.getItem(RANDOM_LIMIT_STORAGE_KEY);

    if (!raw) {
      return {
        date: getTodayKey(),
        count: 0,
      };
    }

    const parsed = JSON.parse(raw);
    const today = getTodayKey();

    if (!parsed || parsed.date !== today) {
      return {
        date: today,
        count: 0,
      };
    }

    return {
      date: today,
      count: Number(parsed.count) || 0,
    };
  } catch {
    return {
      date: getTodayKey(),
      count: 0,
    };
  }
}

function saveRandomLimitState(nextState) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      RANDOM_LIMIT_STORAGE_KEY,
      JSON.stringify(nextState)
    );
  } catch {
    // Jos localStorage ei ole saatavilla, ei rikota käyttöä.
  }
}


// Paranoid-easter egg käynnistyy vain oikeasta
// Black Sabbathin Paranoid-kappaleesta.
const PARANOID_ENDING_KEYS = [
  "songFinder.paranoid.endings.0",
  "songFinder.paranoid.endings.1",
  "songFinder.paranoid.endings.2",
  "songFinder.paranoid.endings.3",
  "songFinder.paranoid.endings.4",
];

function isParanoidRequest(song) {
  const title = normalize(song?.title);
  const artist = normalize(song?.artist);

  return title === "paranoid" && artist.includes("blacksabbath");
}

function isActiveRequest(request) {
  const status = request?.status || "new";

  return status === "new" || status === "seen";
}

function isSameRequestedSong(request, song) {
  const requestTitle = normalize(request?.song_title || request?.title);
  const requestArtist = normalize(request?.artist);
  const songTitle = normalize(song?.title);
  const songArtist = normalize(song?.artist);

  return requestTitle === songTitle && requestArtist === songArtist;
}

function SponsorRotationBlock({
  sponsor,
  sponsorLabel,
  sponsorDescription,
  paranoidStyle = false,
  inline = false,
  compact = false,
  showDescription = true,
}) {
  const { t } = useTranslation("admin");

  if (!sponsor) {
    return null;
  }

  const description = showDescription
    ? sponsorDescription ||
      sponsor.offerText ||
      sponsor.offer_text ||
      sponsor.description ||
      ""
    : "";

  const sponsorLogo = sponsor.logoUrl || sponsor.logo_url || sponsor.logo || "";
  const sponsorName = sponsor.name || "";
  const sponsorLink = sponsor.linkUrl || sponsor.link_url || sponsor.href || "";

  if (inline) {
    const logo = sponsorLogo ? (
      <img
        src={resolveSponsorAssetUrl(sponsorLogo)}
        alt={sponsorName}
        className="h-8 max-w-[4.5rem] object-contain"
        onError={(event) => {
          event.currentTarget.style.display = "none";
        }}
      />
    ) : null;

    return (
      <div className="rounded-2xl border border-zinc-200 bg-white/5 px-3 py-2 theme-dark:border-white/10">
        <div className="flex items-center justify-center gap-3">
          <span className="shrink-0 text-[0.55rem] font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-[#ef4444]">
            {sponsor.label || sponsorLabel || t("songFinder.sponsor.defaultLabel")}
          </span>

          {sponsorLink && logo ? (
            <a href={sponsorLink} target="_blank" rel="noreferrer" className="shrink-0">
              {logo}
            </a>
          ) : (
            logo
          )}

          {sponsorName && (
            <span className="min-w-0 truncate text-xs font-black text-[#241a16] theme-dark:text-white">
              {sponsorName}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={[
        "rounded-[1.35rem] p-4 text-center",
        compact
          ? "rounded-2xl px-3 py-2 text-center"
          : "rounded-[1.35rem] p-4 text-center",
        paranoidStyle
          ? "bg-transparent"
          : "bg-transparent",
      ].join(" ")}
    >
      <p
        className={[
          "text-[0.68rem] font-semibold uppercase tracking-[0.22em]",
          compact
            ? "text-[0.58rem] font-semibold uppercase tracking-[0.2em]"
            : "text-[0.68rem] font-semibold uppercase tracking-[0.22em]",
          paranoidStyle
            ? "text-[#8a5a35] theme-dark:text-[#fecaca]"
            : "text-[#8a5a35] theme-dark:text-[#ef4444]",
        ].join(" ")}
      >
        {sponsor.label || sponsorLabel || t("songFinder.sponsor.defaultLabel")}
      </p>

      <div
        className={
          compact
            ? "mt-1 flex min-h-0 items-center justify-center"
            : "mt-2 flex min-h-12 items-center justify-center sm:min-h-20"
        }
      >
        {sponsorLogo && (
          sponsorLink ? (
            <a href={sponsorLink} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center">
              <img
                src={resolveSponsorAssetUrl(sponsorLogo)}
                alt={sponsorName}
                className={
                  compact
                    ? "max-h-10 max-w-[8rem] object-contain"
                    : "max-h-14 max-w-[10rem] object-contain sm:max-h-20 sm:max-w-[15rem]"
                }
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            </a>
          ) : (
            <img
              src={resolveSponsorAssetUrl(sponsorLogo)}
              alt={sponsorName}
              className="max-h-14 max-w-[10rem] object-contain sm:max-h-20 sm:max-w-[15rem]"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          )
        )}
      </div>

      {sponsorName && (
        <p
          className={
            compact
              ? "mt-1 text-xs font-black tracking-[-0.02em] text-[#241a16] theme-dark:text-white"
              : "mt-2 text-sm font-black tracking-[-0.02em] text-[#241a16] theme-dark:text-white"
          }
        >
          {sponsorName}
        </p>
      )}

      {description && (
        <p
          className={[
            compact
              ? "mx-auto mt-1 max-w-sm truncate text-[0.68rem] font-semibold leading-4"
              : "mx-auto mt-2 max-w-md text-xs font-semibold leading-5 sm:mt-3 sm:text-sm sm:leading-6",
            paranoidStyle
              ? "text-[#5a3824] theme-dark:text-zinc-300"
              : "text-[#5a3824] theme-dark:text-zinc-300",
          ].join(" ")}
        >
          {description}
        </p>
      )}
    </div>
  );
}

export default function SongFinderSection({ config }) {
  const { t, i18n } = useTranslation("admin");

  const publicLocale = i18n.resolvedLanguage?.toLowerCase().startsWith("en")
    ? "en-GB"
    : "fi-FI";


  const canShowRequestStats = config?.features?.requestStatsEnabled === true;

  const canUseSponsors =
    config?.features?.setlistsEnabled === true ||
    config?.features?.requestStatsEnabled === true ||
    config?.features?.sponsorsEnabled === true ||
    config?.performer?.features?.setlistsEnabled === true ||
    config?.performer?.features?.requestStatsEnabled === true ||
    config?.performer?.features?.sponsorsEnabled === true;
  const resultsTopRef = useRef(null);

  const [paranoidiSettings, setParanoidiSettings] = useState(() =>
    getParanoidiSettings()
  );
  const [query, setQuery] = useState("");
  const [artistFilter, setArtistFilter] = useState("");
  const [genreFilter, setGenreFilter] = useState("");
  const [decadeFilter, setDecadeFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [tempoFilter, setTempoFilter] = useState("");
  const [sortMode, setSortMode] = useState("title");
  const [visibleCount, setVisibleCount] = useState(20);
  const [selectedSong, setSelectedSong] = useState(null);
  const [requesterName, setRequesterName] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [wantsRaffle, setWantsRaffle] = useState(false);
  const [publicEvents, setPublicEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");

  // Paranoid-erikoisnäkymän loppulause valitaan satunnaisesti onnistuneen lähetyksen jälkeen.
  const [paranoidEndingKey, setParanoidEndingKey] = useState(
    PARANOID_ENDING_KEYS[0]
  );

  const [isSent, setIsSent] = useState(false);
  const [sentRequestId, setSentRequestId] = useState(null);
  const [isStartingBoost, setIsStartingBoost] = useState(false);
  const [boostError, setBoostError] = useState("");
  const [sentNotice, setSentNotice] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [sponsorIndex, setSponsorIndex] = useState(0);
  const [limitMessage, setLimitMessage] = useState("");
  const [requestFormError, setRequestFormError] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [currentRequestCount, setCurrentRequestCount] = useState(0);
  const [songs, setSongs] = useState(() => demoSongs.map(normalizeSongFromApi));
  const [randomSong, setRandomSong] = useState(null);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(true);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [randomLimitState, setRandomLimitState] = useState(() =>
    getRandomLimitState()
  );
  const [randomLimitMessage, setRandomLimitMessage] = useState("");
  const [topSongRequests, setTopSongRequests] = useState([]);
  const [hasLoadedTopSongRequests, setHasLoadedTopSongRequests] = useState(false);

  const effectiveParanoidiSettings = {
    ...paranoidiSettings,
    ...(config.paranoidiSettings || {}),
    ...(config.settings || {}),
    ...(config.performer?.settings || {}),
  };

const requestSponsors = useMemo(() => {
  const items = canUseSponsors
    ? config.requestSponsors ||
      config.request_sponsors ||
      config.performer?.settings?.requestSponsors ||
      config.performer?.settings?.request_sponsors ||
      config.performer?.requestSponsors ||
      config.performer?.request_sponsors ||
      effectiveParanoidiSettings.requestSponsors ||
      effectiveParanoidiSettings.request_sponsors ||
      fallbackRequestSponsors
    : fallbackRequestSponsors;

    return (Array.isArray(items) ? items : [])
      .slice(0, 5)
      .map((item) => ({
        ...item,
        name: String(item?.name || "").trim(),
        offerText: String(item?.offerText || item?.offer_text || item?.description || "").trim(),
        logoUrl: String(item?.logoUrl || item?.logo_url || item?.logo || "").trim(),
        linkUrl: String(item?.linkUrl || item?.link_url || item?.href || "").trim(),
        active: item?.active !== false,
      }))
      .filter((item) => item.active && (item.name || item.logoUrl || item.offerText));
    }, [
    canUseSponsors,
    config.requestSponsors,
    config.request_sponsors,
    config.performer?.settings?.requestSponsors,
    config.performer?.settings?.request_sponsors,
    config.performer?.requestSponsors,
    config.performer?.request_sponsors,
    effectiveParanoidiSettings.requestSponsors,
    effectiveParanoidiSettings.request_sponsors,
  ]);

  const maxRequestsPerSet =
    Number(effectiveParanoidiSettings.maxRequestsPerSet) || 50;
  const isRequestLimitFull = currentRequestCount >= maxRequestsPerSet;
  const publicSongLanguageMode =
    effectiveParanoidiSettings.publicSongLanguageMode || "all";

  const randomEnabledRaw =
    effectiveParanoidiSettings.randomEnabled ??
    effectiveParanoidiSettings.random_enabled ??
    true;

  const isRandomSongEnabled =
    randomEnabledRaw === true ||
    randomEnabledRaw === 1 ||
    String(randomEnabledRaw).trim().toLowerCase() === "true" ||
    String(randomEnabledRaw).trim() === "1";

  const isRequestRaffleEnabled = Boolean(
    effectiveParanoidiSettings.requestRaffleEnabled ??
      effectiveParanoidiSettings.request_raffle_enabled ??
      config?.performer?.settings?.requestRaffleEnabled ??
      config?.performer?.settings?.request_raffle_enabled ??
      config?.requestRaffleEnabled ??
      config?.request_raffle_enabled ??
      false
  );

  const paidRequestBoostsEnabled = Boolean(
    effectiveParanoidiSettings.paidRequestBoostsEnabled ??
      effectiveParanoidiSettings.paid_request_boosts_enabled ??
      false
  );

  const paidRequestBoostAmountCents =
    Number(
      effectiveParanoidiSettings.paidRequestBoostAmountCents ??
        effectiveParanoidiSettings.paid_request_boost_amount_cents ??
        300
    ) || 300;

  const paidRequestBoostPrice = new Intl.NumberFormat(publicLocale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: paidRequestBoostAmountCents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(paidRequestBoostAmountCents / 100);

    useEffect(() => {
    if (!isRequestRaffleEnabled && wantsRaffle) {
      setWantsRaffle(false);
    setSelectedEventId("");
    }
  }, [isRequestRaffleEnabled, wantsRaffle]);
  useEffect(() => {
    let isMounted = true;

    async function loadPublicEvents() {
      try {
        const data = await getPublicPerformerEvents(config.performerSlug);
        const items = Array.isArray(data.items) ? data.items : [];

        if (isMounted) {
          setPublicEvents(items);
        }
      } catch (error) {
        console.error("Tapahtumien haku julkiselle lomakkeelle epäonnistui:", error);

        if (isMounted) {
          setPublicEvents([]);
        }
      }
    }

    loadPublicEvents();

    const interval = window.setInterval(loadPublicEvents, 60000);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, [config.performerSlug]);

  useEffect(() => {
    if (!selectedEventId) {
      return;
    }

    const stillExists = publicEvents.some(
      (eventItem) => String(eventItem.id) === String(selectedEventId)
    );

    if (!stillExists) {
      setSelectedEventId("");
    }
  }, [publicEvents, selectedEventId]);

  const requestableSongs = useMemo(() => {
    return songs.filter(
      (song) =>
        getSongIsRequestable(song) &&
        isSongAllowedByLanguageMode(song, publicSongLanguageMode)
    );
  }, [songs, publicSongLanguageMode]);

  const artistOptions = useMemo(() => {
    const artistMap = new Map();

    requestableSongs.forEach((song) => {
      const label = String(song.artist || "").trim();

      if (!label) {
        return;
      }

      const key = normalize(label);

      if (!key) {
        return;
      }

      const current = artistMap.get(key);

      if (!current) {
        artistMap.set(key, {
          key,
          label,
        });
        return;
      }

      /*
       * Jos samasta artistista löytyy eri kirjainkokoversioita
       * (esim. "Don huonot" / "Don Huonot"), suositaan siistimpää
       * otsikkomaista kirjoitusasua vain näyttönimenä.
       * Suodatus käyttää aina normalisoitua avainta.
       */
      const getDisplayScore = (value) =>
        String(value)
          .split(/\s+/)
          .filter(Boolean)
          .reduce((score, word) => {
            if (/^[A-ZÅÄÖ]/.test(word)) {
              return score + 1;
            }

            return score;
          }, 0);

      if (getDisplayScore(label) > getDisplayScore(current.label)) {
        artistMap.set(key, {
          key,
          label,
        });
      }
    });

    return Array.from(artistMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label, publicLocale)
    );
  }, [requestableSongs, publicLocale]);

  const selectedArtistLabel = useMemo(
    () =>
      artistOptions.find((artistOption) => artistOption.key === artistFilter)
        ?.label || "",
    [artistOptions, artistFilter]
  );

  const genreOptions = useMemo(
    () =>
      Array.from(
        new Set(
          requestableSongs
            .map((song) => String(song.genre || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, publicLocale)),
    [requestableSongs, publicLocale]
  );

  const decadeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          requestableSongs
            .map((song) => String(getSongDecade(song) || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, publicLocale, { numeric: true })),
    [requestableSongs, publicLocale]
  );

  const languageOptions = useMemo(
    () =>
      Array.from(
        new Set(
          requestableSongs
            .map((song) => String(getSongLanguage(song) || "").trim())
            .filter((value) => value && value !== "unknown")
        )
      ).sort((a, b) => a.localeCompare(b, publicLocale)),
    [requestableSongs, publicLocale]
  );

  const tempoOptions = useMemo(
    () =>
      Array.from(
        new Set(
          requestableSongs
            .map((song) => String(song.tempo || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, publicLocale, { numeric: true })),
    [requestableSongs, publicLocale]
  );

  const hasActiveFilters = Boolean(
    artistFilter ||
      genreFilter ||
      decadeFilter ||
      languageFilter ||
      tempoFilter
  );

  function resetSongFilters() {
    setArtistFilter("");
    setGenreFilter("");
    setDecadeFilter("");
    setLanguageFilter("");
    setTempoFilter("");
    setSortMode("title");
    setVisibleCount(20);
  }

  function getFilterSelectClass(isActive) {
    return [
      "min-w-0 rounded-xl border bg-[#08080a] px-3 py-3 text-xs font-bold text-white outline-none transition focus:border-red-400/50",
      isActive
        ? "border-red-400/55 shadow-[0_0_0_1px_rgba(239,68,68,0.10),0_0_18px_rgba(239,68,68,0.08)]"
        : "border-white/10",
    ].join(" ");
  }

  function getSongCountLabel(count) {
    return t("songFinder.search.songCount", { count });
  }

  function getVisibleSongCountLabel(filteredCount, totalCount) {
    return t("songFinder.search.showingCount", {
      filtered: filteredCount,
      total: totalCount,
      count: totalCount,
    });
  }

  function clearSingleFilter(filterName) {
    if (filterName === "artist") {
      setArtistFilter("");
    }

    if (filterName === "genre") {
      setGenreFilter("");
    }

    if (filterName === "decade") {
      setDecadeFilter("");
    }

    if (filterName === "language") {
      setLanguageFilter("");
    }

    if (filterName === "tempo") {
      setTempoFilter("");
    }

    setVisibleCount(20);
  }

  function getLanguageFilterLabel(value) {
    const normalizedValue = String(value || "").trim().toLowerCase();

    if (normalizedValue === "fi" || normalizedValue === "fin" || normalizedValue === "finnish") {
      return t("songFinder.search.languages.fi");
    }

    if (normalizedValue === "en" || normalizedValue === "eng" || normalizedValue === "english") {
      return t("songFinder.search.languages.en");
    }

    if (normalizedValue === "sv" || normalizedValue === "swe" || normalizedValue === "swedish") {
      return t("songFinder.search.languages.sv");
    }

    return value;
  }

  useEffect(() => {
    if (!randomSong) {
      return;
    }

    const randomSongStillAllowed = requestableSongs.some(
      (song) =>
        normalize(song.title) === normalize(randomSong.title) &&
        normalize(song.artist) === normalize(randomSong.artist)
    );

    if (!randomSongStillAllowed) {
      setRandomSong(null);
    }
  }, [requestableSongs, randomSong]);

  useEffect(() => {
    if (isRandomSongEnabled) {
      return;
    }

    setRandomSong(null);
    setRandomLimitMessage("");
    setIsAdvancedSearchOpen(true);
  }, [isRandomSongEnabled]);

  function getSponsorDescription(sponsor) {
    if (!sponsor) {
      return "";
    }

    return (
      config.sectionLabels?.sponsorDescriptions?.[sponsor.name] ||
      sponsor.description ||
      ""
    );
  }

  function getQuickSearchLabel(item) {
    const labels = config.sectionLabels?.quickSearchLabels || {};
    const value = String(item || "");

    return (
      labels[value] ||
      labels[value.toUpperCase()] ||
      labels[value.toLowerCase()] ||
      value
    );
  }

  function getMoodLabel(tag) {
    const labels = config.sectionLabels?.songMoodLabels || {};
    const value = String(tag || "");

    return (
      labels[value] ||
      labels[value.toLowerCase()] ||
      labels[value.toUpperCase()] ||
      value
    );
  }

  const filteredSongs = useMemo(() => {
    const search = query.trim();

    let nextSongs = requestableSongs.filter((song) => {
      if (
        artistFilter &&
        normalize(String(song.artist || "").trim()) !== artistFilter
      ) {
        return false;
      }

      if (
        genreFilter &&
        String(song.genre || "").trim() !== genreFilter
      ) {
        return false;
      }

      if (
        decadeFilter &&
        String(getSongDecade(song) || "").trim() !== decadeFilter
      ) {
        return false;
      }

      if (
        languageFilter &&
        String(getSongLanguage(song) || "").trim() !== languageFilter
      ) {
        return false;
      }

      if (
        tempoFilter &&
        String(song.tempo || "").trim() !== tempoFilter
      ) {
        return false;
      }

      return true;
    });

    if (search) {
      nextSongs = nextSongs
        .map((song) => ({
          ...song,
          searchScore: scoreSong(song, search),
        }))
        .filter((song) => song.searchScore > 0);
    }

    return [...nextSongs].sort((a, b) => {
      if (sortMode === "artist") {
        const artistCompare = String(a.artist || "").localeCompare(
          String(b.artist || ""),
          publicLocale
        );

        if (artistCompare !== 0) {
          return artistCompare;
        }
      }

      return String(a.title || "").localeCompare(
        String(b.title || ""),
        publicLocale
      );
    });
  }, [
    query,
    requestableSongs,
    publicLocale,
    artistFilter,
    genreFilter,
    decadeFilter,
    languageFilter,
    tempoFilter,
    sortMode,
  ]);

  const hasSearch = query.trim().length > 0;

  const visibleSongs =
    isAdvancedSearchOpen
      ? filteredSongs.slice(0, visibleCount)
      : [];

  const hasMoreSongs =
    isAdvancedSearchOpen &&
    filteredSongs.length > visibleCount;

  const hasExpandedSongs =
    isAdvancedSearchOpen &&
    visibleCount > 20;

  const suggestedSongs = [];

  const activeSponsor = requestSponsors.length
    ? requestSponsors[sponsorIndex % requestSponsors.length]
    : null;

  function getRandomSponsorIndex() {
    const sponsorCount = requestSponsors.length;

    if (sponsorCount <= 1) {
      return 0;
    }

    let nextIndex = Math.floor(Math.random() * sponsorCount);

    if (nextIndex === sponsorIndex) {
      nextIndex = (nextIndex + 1) % sponsorCount;
    }

    return nextIndex;
  }
  const isSelectedParanoid = isParanoidRequest(selectedSong);

  function updateSearch(value) {
    setQuery(value);
    setVisibleCount(5);
  }

  function getLocalizedRequestError(error) {
    const code = String(error?.code || error?.payload?.code || "").trim();

    if (code === "ACTIVE_REQUEST_ALREADY_EXISTS") {
      const existingRequest =
        error?.existingRequest ||
        error?.payload?.existingRequest ||
        error?.payload?.existing_request ||
        null;

      const songTitle = String(
        existingRequest?.songTitle ||
          existingRequest?.song_title ||
          existingRequest?.title ||
          ""
      ).trim();

      return songTitle
        ? t("songFinder.errors.activeRequestAlreadyExistsWithSong", {
            songTitle,
            defaultValue:
              publicLocale === "en-GB"
                ? `You already have a song request in the queue: ${songTitle}. You can send a new request once the previous one has been handled.`
                : `Sinulla on jo biisitoive jonossa: ${songTitle}. Voit lähettää uuden toiveen, kun edellinen on käsitelty.`,
          })
        : t("songFinder.errors.activeRequestAlreadyExists", {
            defaultValue:
              publicLocale === "en-GB"
                ? "You already have a song request in the queue. You can send a new request once the previous one has been handled."
                : "Sinulla on jo biisitoive jonossa. Voit lähettää uuden toiveen, kun edellinen on käsitelty.",
          });
    }

    const keyByCode = {
      EVENT_NOT_ACTIVE: "songFinder.errors.eventNotActive",
      EVENT_REQUESTS_NOT_OPEN: "songFinder.errors.eventRequestsNotOpen",
      EVENT_REQUESTS_CLOSED: "songFinder.errors.eventRequestsClosed",
      EVENT_ENDED: "songFinder.errors.eventEnded",
      EVENT_REQUEST_LIMIT_REACHED: "songFinder.errors.eventRequestLimitReached",
      active_request_limit_per_ip: "songFinder.errors.activeRequestLimitPerIp",
      active_request_limit_per_set: "songFinder.errors.activeRequestLimitPerSet",
      SAVED_REQUEST_ID_MISSING: "songFinder.errors.savedRequestIdMissing",
    };

    return keyByCode[code]
      ? t(keyByCode[code])
      : t("songFinder.errors.sendFailed");
  }

  function getLocalizedBoostError(error) {
    const code = String(error?.code || error?.payload?.code || "").trim();

    const keyByCode = {
      BOOST_CHECKOUT_URL_MISSING: "songFinder.boost.missingCheckoutUrl",
      STRIPE_CONNECT_NOT_READY: "songFinder.boost.stripeNotReady",
      SONG_REQUEST_ALREADY_BOOSTED: "songFinder.boost.alreadyBoosted",
      BOOST_PAYMENT_ALREADY_PENDING: "songFinder.boost.paymentPending",
      BOOST_CHECKOUT_FAILED: "songFinder.boost.startFailed",
    };

    return keyByCode[code]
      ? t(keyByCode[code])
      : t("songFinder.boost.startFailed");
  }

  function pickRandomSong() {
    if (requestableSongs.length === 0) {
      return;
    }

    const currentLimitState = getRandomLimitState();

    if (currentLimitState.count >= RANDOM_DAILY_LIMIT) {
      setRandomLimitState(currentLimitState);
        setRandomLimitMessage(t("songFinder.random.dailyLimit"));
      return;
    }

    const nextLimitState = {
      date: currentLimitState.date,
      count: currentLimitState.count + 1,
    };

    saveRandomLimitState(nextLimitState);
    setRandomLimitState(nextLimitState);
    setRandomLimitMessage("");

    const availableSongs =
      randomSong && requestableSongs.length > 1
        ? requestableSongs.filter(
            (song) =>
              normalize(song.title) !== normalize(randomSong.title) ||
              normalize(song.artist) !== normalize(randomSong.artist)
          )
        : requestableSongs;

    const nextSong =
      availableSongs[Math.floor(Math.random() * availableSongs.length)];

    setRandomSong(nextSong);

      trackEvent("song_random", {
        song_title: nextSong.title,
        artist: nextSong.artist,
      });

      setSponsorIndex(getRandomSponsorIndex());
  }

  function showLimitMessage(message) {
    setLimitMessage(message);

    window.setTimeout(() => {
      setLimitMessage("");
    }, 3500);
  }

  function showToast(message) {
    setToastMessage(message);

    window.setTimeout(() => {
      setToastMessage("");
    }, 3200);
  }

  async function refreshRequestCount() {
    try {
      const currentRequests = await getSongRequests(config.performerSlug, config.roomSlug);
      const activeCount = (currentRequests.items || []).filter(isActiveRequest).length;

      setCurrentRequestCount(activeCount);
    } catch (error) {
      console.error("Toivemäärän haku epäonnistui:", error);
    }
  }

  function openRequestModal(song) {
    setSelectedSong(song);
    setRequesterName("");
    setRequestMessage("");
    setWantsRaffle(false);
    setSelectedEventId("");
    setRequestFormError("");
    setIsSent(false);
    setSentNotice("");
    setSentRequestId(null);
    setIsStartingBoost(false);
    setBoostError("");

    // Aloitetaan jokainen uusi modal puhtaalta Paranoid-tilalta.
    setParanoidEndingKey(PARANOID_ENDING_KEYS[0]);

    setSponsorIndex(getRandomSponsorIndex());
  }

  function openFreeRequestModal() {
    const freeSongTitle = query.trim();

    if (!freeSongTitle) {
      return;
    }

    openRequestModal({
      title: freeSongTitle,
      artist: "",
      requestType: "free",
      request_type: "free",
    });
  }

  function closeRequestModal() {
    setSelectedSong(null);
    setRequesterName("");
    setRequestMessage("");
    setWantsRaffle(false);
    setSelectedEventId("");
    setIsSent(false);
    setSentNotice("");
    setSentRequestId(null);
    setIsStartingBoost(false);
    setBoostError("");
    setSponsorIndex(0);
  }

  async function startPaidRequestBoost() {
    if (!sentRequestId || isStartingBoost) {
      return;
    }

    setIsStartingBoost(true);
    setBoostError("");

    try {
      const data = await createSongRequestBoostCheckout(
        sentRequestId,
        config.performerSlug
      );

      const checkoutUrl = String(data?.url || "").trim();

      if (!checkoutUrl) {
        const checkoutUrlError = new Error("Missing checkout URL");
        checkoutUrlError.code = "BOOST_CHECKOUT_URL_MISSING";
        throw checkoutUrlError;
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      console.error(
        "Kappaletoiveen näkyvyysnoston aloitus epäonnistui:",
        error
      );

      setBoostError(getLocalizedBoostError(error));

      setIsStartingBoost(false);
    }
  }

  function renderPaidRequestBoostCard() {
    if (!paidRequestBoostsEnabled || !sentRequestId) {
      return null;
    }

    return (
      <div className="mt-5 rounded-[1.5rem] border border-red-300/30 bg-red-500/[0.08] p-5">
        <p className="text-lg font-black text-zinc-950 theme-dark:text-white">
          {t("songFinder.boost.title", { price: paidRequestBoostPrice })}
        </p>

        <p className="mt-2 text-sm font-semibold leading-6 text-zinc-700 theme-dark:text-zinc-300">
          {t("songFinder.boost.description")}
        </p>

        <p className="mt-2 text-xs font-semibold leading-5 text-zinc-500 theme-dark:text-zinc-400">
          {t("songFinder.boost.disclaimer")}
        </p>

        {boostError && (
          <p className="mt-3 text-sm font-bold text-red-700 theme-dark:text-red-300">
            {boostError}
          </p>
        )}

        <ParanoidiButton
          type="button"
          variant="primary"
          size="md"
          disabled={isStartingBoost}
          onClick={startPaidRequestBoost}
          className="mt-4 w-full !px-6 !py-3 disabled:pointer-events-none disabled:opacity-60"
        >
          {isStartingBoost
            ? t("songFinder.boost.opening")
            : t("songFinder.boost.title", { price: paidRequestBoostPrice })}
        </ParanoidiButton>
      </div>
    );
  }
  async function submitRequest(event) {
    event.preventDefault();

    if (!selectedSong || isSubmittingRequest) {
      return;
    }

    if (!requesterName.trim()) {
      setRequestFormError(t("songFinder.messages.nameRequired"));
      return;
    }

    setIsSubmittingRequest(true);

    try {
      const maxRequests =
        Number(effectiveParanoidiSettings.maxRequestsPerSet) || 50;

      const activeCount = Number(currentRequestCount || 0);

    if (activeCount >= maxRequests) {
        setSelectedSong(null);
        setRequesterName("");
        setRequestMessage("");
        setIsSent(false);

        showLimitMessage(t("songFinder.limit.submitFull"));

        return;
      }

      const nextSentNotice = t("songFinder.modal.sentText");

        setRequestFormError("");

        const isFreeSongRequest =
          selectedSong.requestType === "free" || selectedSong.request_type === "free";

        const createResult = await createSongRequest({
          event_id: selectedEventId || null,
          eventId: selectedEventId || null,
          song_id: isFreeSongRequest ? null : selectedSong.id,
          songId: isFreeSongRequest ? null : selectedSong.id,
          song_title: selectedSong.title,
          artist: selectedSong.artist,
          requester_name: requesterName.trim(),
          requester_location: "",
          message: requestMessage.trim(),
          raffle_opt_in: isRequestRaffleEnabled && wantsRaffle,
          raffleOptIn: isRequestRaffleEnabled && wantsRaffle,
          request_type: isFreeSongRequest ? "free" : "library",
        }, config.performerSlug, config.roomSlug);

        const createdRequestId = Number(createResult?.item?.id);

        if (!Number.isInteger(createdRequestId) || createdRequestId < 1) {
          const requestIdError = new Error("Missing saved request ID");
          requestIdError.code = "SAVED_REQUEST_ID_MISSING";
          throw requestIdError;
        }

        setSentRequestId(createdRequestId);

        /*
        * Paranoid on Paranoidin oma easter egg.
        * Erikoisnäkymä käynnistyy vasta kun toive on oikeasti tallennettu.
        */
        if (isParanoidRequest(selectedSong)) {
          const randomEndingKey =
            PARANOID_ENDING_KEYS[
              Math.floor(Math.random() * PARANOID_ENDING_KEYS.length)
            ];

          setParanoidEndingKey(randomEndingKey);

          // Kevyt hälytysvärinä tuetuilla mobiililaitteilla.
          if (
            typeof window !== "undefined" &&
            typeof window.navigator?.vibrate === "function"
          ) {
            window.navigator.vibrate([80, 50, 140]);
          }
        }

        trackSongRequest(selectedSong.title, selectedSong.artist, {
        request_type: isFreeSongRequest ? "free" : "library",
      });

      if (
        randomSong &&
        normalize(randomSong.title) === normalize(selectedSong.title) &&
        normalize(randomSong.artist) === normalize(selectedSong.artist)
      ) {
        setRandomSong(null);
      }

      setSentNotice(nextSentNotice);
      setIsSent(true);
      showToast(t("songFinder.messages.sentToast"));
      setCurrentRequestCount((current) =>
        Math.min(maxRequests, Number(current || 0) + 1)
      );
      void refreshRequestCount();
    } catch (error) {
      console.error("Biisitoiveen lähetys epäonnistui:", error);

      setRequestFormError(getLocalizedRequestError(error));
    } finally {
      setIsSubmittingRequest(false);
    }
  }

  useEffect(() => {
    let isMounted = true;

    fetchParanoidiSettings().then((settings) => {
      if (isMounted) {
        setParanoidiSettings(settings);
      }
    });

    const unsubscribe = subscribeParanoidiSettings(setParanoidiSettings);

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    let intervalId = 0;

    async function loadSongs() {
      try {
        const data = await getSongs(config.performerSlug);
        const apiSongs = (data.items || [])
          .map(normalizeSongFromApi)
          .filter(getSongIsRequestable);

        if (isMounted) {
          setSongs(apiSongs);
        }
      } catch (error) {
        console.error("Biisikirjaston haku epäonnistui:", error);
      }
    }

    loadSongs();
    intervalId = window.setInterval(loadSongs, 60000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [config.performerSlug, config.roomSlug]);

  useEffect(() => {
    refreshRequestCount();

    const interval = window.setInterval(refreshRequestCount, 30000);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadTopSongRequests() {
      try {
        const data = await getSongRequests(config.performerSlug, config.roomSlug);
        const requests = Array.isArray(data.items) ? data.items : [];

        const grouped = new Map();

        requests.forEach((request) => {
          const title = request.songTitle || request.song_title || "";
          const artist = request.songArtist || request.song_artist || request.artist || "";

          if (!title.trim()) {
            return;
          }

          const key = `${normalize(title)}|${normalize(artist)}`;
          const current = grouped.get(key) || {
            songTitle: title,
            song_title: title,
            artist,
            totalRequests: 0,
            total_requests: 0,
          };

          const requestCount = Number(
            request.requestCount || request.request_count || 1
          );

          current.totalRequests += Number.isFinite(requestCount) ? requestCount : 1;
          current.total_requests = current.totalRequests;

          grouped.set(key, current);
        });

        const items = Array.from(grouped.values())
          .sort((a, b) => b.totalRequests - a.totalRequests)
          .slice(0, 3)
          .map((item, index) => ({
            ...item,
            position: index + 1,
          }));

        if (isMounted) {
          setTopSongRequests(items);
          setHasLoadedTopSongRequests(true);
        }
      } catch (error) {
        console.error("Top 3 -toiveiden haku epäonnistui:", error);

        if (isMounted) {
          setTopSongRequests([]);
          setHasLoadedTopSongRequests(true);
        }
      }
    }

    loadTopSongRequests();

    const interval = window.setInterval(loadTopSongRequests, 60000);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, [config.performerSlug, config.roomSlug]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        closeRequestModal();
      }
    }

    if (selectedSong) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedSong]);

  useEffect(() => {
    if (!selectedSong || isSent) {
      return undefined;
    }

    // LiteSpeed sulkee käyttämättömän HTTP/1.1-yhteyden noin 5 sekunnissa.
    // Pidetään API-yhteys lämpimänä vain biisitoivemodaalin ollessa auki,
    // jotta varsinainen POST voi hyödyntää jo muodostettua yhteyttä.
    const warmConnection = () => {
      void warmPublicApiConnection();
    };

    warmConnection();

    const interval = window.setInterval(warmConnection, 4000);

    return () => {
      window.clearInterval(interval);
    };
  }, [selectedSong, isSent]);

  useEffect(() => {
    function updateViewportSize() {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    updateViewportSize();
    window.addEventListener("resize", updateViewportSize);

    return () => {
      window.removeEventListener("resize", updateViewportSize);
    };
  }, []);

  useEffect(() => {
    if (!selectedSong) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.dataset.paranoidiRequestModalScrollLock = "true";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      delete document.body.dataset.paranoidiRequestModalScrollLock;
    };
  }, [selectedSong]);
  return (
  <section
    id="biisihaku"
    className="bg-[#050706] px-3 pb-64 pt-20 text-white sm:px-6 sm:py-24"
  >
  <div className="mx-auto max-w-7xl">
    <Reveal>
      <div className="grid gap-4 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-red-400/25 bg-red-500/10 px-3 py-1.5 shadow-[0_10px_26px_rgba(185,28,28,0.12)]">
                <span className="relative flex h-5 w-5 items-center justify-center">
                  <span className="absolute h-5 w-5 animate-ping rounded-full bg-red-400 opacity-50 [animation-duration:2.4s]" />
                  <span className="block h-4 w-4 animate-pulse rounded-full bg-[#ef4444] shadow-[0_0_18px_rgba(239,68,68,0.75)] [animation-duration:1.4s]" />
                </span>

                <span className="text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-red-400">
                  {t("songFinder.hero.open")}
                </span>

                <span className="hidden h-3 w-px bg-red-500/25 sm:block" />

                <span className="hidden text-[0.68rem] font-bold uppercase tracking-[0.18em] text-[#fecaca] sm:inline">
                  {isRequestLimitFull
                    ? t("songFinder.hero.fullShort")
                    : t("songFinder.hero.openShort")}
                </span>
              </div>

              <h2 className="mt-4 max-w-2xl text-4xl font-semibold leading-[0.98] tracking-[-0.055em] text-white sm:text-6xl">
                {t("songFinder.hero.title", {
                  artist: config.artistName || "Paranoidi",
                })}
              </h2>

              <p className="mt-4 max-w-xl text-lg font-semibold leading-8 text-[#ef4444]">
                {t("songFinder.hero.subtitle")}
              </p>
            </div>

            <div className="max-w-2xl lg:ml-auto">
              <p className="text-base leading-8 text-zinc-400">
                {isRandomSongEnabled
                  ? t("songFinder.hero.introWithRandom")
                  : t("songFinder.hero.introSearchOnly")}
              </p>
            </div>
         
          </div>
    </Reveal>

        <div className="relative">
          <div
            className={
              isRequestLimitFull
                ? "pointer-events-none select-none blur-[2px] opacity-35 transition duration-300"
                : "transition duration-300"
            }
          >
            {isRandomSongEnabled && (
              <Reveal delay={120}>
                <div className="relative mt-12 w-full overflow-hidden rounded-[2rem] border border-white/10 bg-[#0b0b0e] p-[1px] shadow-[0_32px_90px_rgba(0,0,0,0.62),0_12px_30px_rgba(0,0,0,0.42),0_0_45px_rgba(239,68,68,0.06)]">
                  {/* Ulkoreunan valaistus tekee kortista fyysisemmän. */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  />

                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-32 left-1/2 h-64 w-[34rem] -translate-x-1/2 rounded-full bg-red-500/15 blur-[80px]"
                  />

                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-28 right-12 h-52 w-52 rounded-full bg-red-950/30 blur-[70px]"
                  />

                  <div className="relative rounded-[calc(2rem-1px)] bg-[linear-gradient(145deg,#17171a_0%,#0d0d10_48%,#08080a_100%)] px-6 py-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-16px_40px_rgba(0,0,0,0.28)] sm:px-8 sm:py-10">
                    <div className="relative z-10 mx-auto max-w-2xl text-center">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-red-400/25 bg-[linear-gradient(145deg,rgba(239,68,68,0.22),rgba(127,29,29,0.08))] text-2xl text-red-300 shadow-[0_16px_34px_rgba(0,0,0,0.42),0_0_28px_rgba(239,68,68,0.16),inset_0_1px_0_rgba(255,255,255,0.14),inset_0_-6px_14px_rgba(0,0,0,0.26)]">
                        <FaDice aria-hidden="true" />
                      </div>

                        <p className="mt-5 text-xs font-black uppercase tracking-[0.24em] text-red-400">
                          {t("songFinder.random.eyebrow")}
                        </p>

                        <h3 className="mt-3 text-3xl font-black tracking-[-0.045em] text-white">
                          {t("songFinder.random.title")}
                        </h3>

                        <p className="mx-auto mt-3 max-w-2xl text-sm font-semibold leading-7 text-zinc-400">
                          {t("songFinder.random.description")}
                        </p>

                        <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                          {!isAdvancedSearchOpen && (
                            <ParanoidiButton
                              type="button"
                              variant="primary"
                              size="md"
                              onClick={() => {
                                setIsAdvancedSearchOpen(true);
                                trackEvent("song_search_open");
                              }}
                              className="!px-6 !py-3 shadow-[0_12px_30px_rgba(239,68,68,0.18)]"
                            >
                              {t("songFinder.random.browse")}
                            </ParanoidiButton>
                          )}

                          <ParanoidiButton
                            type="button"
                            variant={isAdvancedSearchOpen ? "primary" : "secondary"}
                            size="md"
                            onClick={pickRandomSong}
                            className="!px-6 !py-3"
                          >
                            {t("songFinder.random.button")}
                          </ParanoidiButton>
                        </div>

                      {randomLimitMessage && !randomSong && (
                        <p className="mt-5 rounded-2xl border border-red-500/20 bg-black/35 px-4 py-3 text-sm font-semibold leading-6 text-red-200 shadow-[inset_0_2px_10px_rgba(0,0,0,0.32)]">
                          {randomLimitMessage}
                        </p>
                      )}

                      {randomSong && (
                        <div className="mx-auto mt-7 max-w-xl rounded-[1.5rem] border border-white/10 bg-black/35 p-5 text-center shadow-[0_14px_30px_rgba(0,0,0,0.32),inset_0_1px_0_rgba(255,255,255,0.06),inset_0_3px_18px_rgba(0,0,0,0.32)]">
                          <p className="text-xs font-black uppercase tracking-[0.18em] text-red-400">
                            {t("songFinder.random.result")}
                          </p>

                          <h4 className="mt-2 text-2xl font-black tracking-[-0.04em] text-white">
                            {randomSong.title}
                          </h4>

                          <p className="mt-1 text-sm font-semibold text-zinc-400">
                            {[randomSong.artist].filter(Boolean).join(" · ")}
                          </p>

                          <div className="mt-5 flex justify-center">
                            <ParanoidiButton
                              type="button"
                              variant="primary"
                              size="sm"
                              onClick={() => openRequestModal(randomSong)}
                              className="!px-5 !py-2.5 shadow-[0_10px_24px_rgba(239,68,68,0.16)]"
                            >
                              {t("songFinder.random.requestThis")}
                            </ParanoidiButton>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            )}

            {isAdvancedSearchOpen && (
              <Reveal delay={160}>
                <div
                  ref={resultsTopRef}
                  className="relative mt-8 w-full overflow-hidden rounded-[2rem] border border-white/10 bg-[#0b0b0e] p-[1px] shadow-[0_30px_80px_rgba(0,0,0,0.58),0_10px_26px_rgba(0,0,0,0.38)]"
                >
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent"
                  />

                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-28 -top-28 h-64 w-64 rounded-full bg-red-600/10 blur-[75px]"
                  />

                  <div className="relative rounded-[calc(2rem-1px)] bg-[linear-gradient(145deg,#161619_0%,#0d0d10_55%,#08080a_100%)] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-14px_36px_rgba(0,0,0,0.26)] sm:p-8">
                    <div className="relative z-10">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-red-400/20 bg-red-500/10 text-red-300 shadow-[0_10px_24px_rgba(0,0,0,0.34),inset_0_1px_0_rgba(255,255,255,0.10)]">
                            <FaSearch aria-hidden="true" />
                          </div>

                          <div>
                            <p className="text-[0.65rem] font-black uppercase tracking-[0.2em] text-red-400">
                              {t("songFinder.search.eyebrow")}
                            </p>

                            <label
                              htmlFor="song-search"
                              className="mt-0.5 block text-lg font-black text-white"
                            >
                              {t("songFinder.search.title")}
                            </label>
                          </div>
                        </div>
                      </div>

                      <p className="mt-4 text-sm font-semibold leading-6 text-zinc-400">
                        {t("songFinder.search.description")}
                      </p>

                      <div className="mt-5 rounded-[1.35rem] border border-white/10 bg-black/35 p-2 shadow-[inset_0_3px_18px_rgba(0,0,0,0.48),0_1px_0_rgba(255,255,255,0.04)]">
                        <input
                          id="song-search"
                          type="search"
                          value={query}
                          onChange={(event) => updateSearch(event.target.value)}
                          placeholder={t("songFinder.search.placeholder")}
                          className="w-full rounded-2xl border border-white/10 bg-[#08080a] px-4 py-4 text-sm font-semibold text-white outline-none transition placeholder:text-zinc-500 focus:border-red-400/50 focus:bg-black focus:ring-2 focus:ring-red-500/10 sm:px-5 sm:text-base"
                        />
                      </div>

                      <div className="mt-4 rounded-[1.35rem] border border-white/10 bg-black/25 p-3 sm:p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.18em] text-red-400">
                              {t("songFinder.search.filters.title")}
                            </p>
                            <p className="mt-1 text-xs font-semibold text-zinc-500">
                              {getVisibleSongCountLabel(
                                filteredSongs.length,
                                requestableSongs.length
                              )}
                            </p>
                          </div>

                          {(hasActiveFilters || sortMode !== "title") && (
                            <button
                              type="button"
                              onClick={resetSongFilters}
                              className="rounded-xl border border-white/10 px-3 py-2 text-xs font-black text-zinc-300 transition hover:border-red-400/30 hover:text-white"
                            >
                              {t("songFinder.search.filters.reset")}
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-6">
                          <select
                            value={artistFilter}
                            onChange={(event) => {
                              setArtistFilter(event.target.value);
                              setVisibleCount(20);
                            }}
                            aria-label={t("songFinder.search.filters.artist")}
                            className={getFilterSelectClass(Boolean(artistFilter))}
                          >
                            <option value="">{t("songFinder.search.filters.allArtists")}</option>
                            {artistOptions.map((artistOption) => (
                              <option
                                key={artistOption.key}
                                value={artistOption.key}
                              >
                                {artistOption.label}
                              </option>
                            ))}
                          </select>

                          <select
                            value={genreFilter}
                            onChange={(event) => {
                              setGenreFilter(event.target.value);
                              setVisibleCount(20);
                            }}
                            aria-label={t("songFinder.search.filters.genre")}
                            className={getFilterSelectClass(Boolean(genreFilter))}
                          >
                            <option value="">{t("songFinder.search.filters.allGenres")}</option>
                            {genreOptions.map((genre) => (
                              <option key={genre} value={genre}>
                                {genre}
                              </option>
                            ))}
                          </select>

                          <select
                            value={decadeFilter}
                            onChange={(event) => {
                              setDecadeFilter(event.target.value);
                              setVisibleCount(20);
                            }}
                            aria-label={t("songFinder.search.filters.decade")}
                            className={getFilterSelectClass(Boolean(decadeFilter))}
                          >
                            <option value="">{t("songFinder.search.filters.allDecades")}</option>
                            {decadeOptions.map((decade) => (
                              <option key={decade} value={decade}>
                                {decade}
                              </option>
                            ))}
                          </select>

                          <select
                            value={languageFilter}
                            onChange={(event) => {
                              setLanguageFilter(event.target.value);
                              setVisibleCount(20);
                            }}
                            aria-label={t("songFinder.search.filters.language")}
                            className={getFilterSelectClass(Boolean(languageFilter))}
                          >
                            <option value="">{t("songFinder.search.filters.allLanguages")}</option>
                            {languageOptions.map((language) => (
                              <option key={language} value={language}>
                                {getLanguageFilterLabel(language)}
                              </option>
                            ))}
                          </select>

                          {tempoOptions.length > 0 && (
                            <select
                              value={tempoFilter}
                              onChange={(event) => {
                                setTempoFilter(event.target.value);
                                setVisibleCount(20);
                              }}
                              aria-label={t("songFinder.search.filters.tempo")}
                              className={getFilterSelectClass(Boolean(tempoFilter))}
                            >
                              <option value="">{t("songFinder.search.filters.allTempos")}</option>
                              {tempoOptions.map((tempo) => (
                                <option key={tempo} value={tempo}>
                                  {tempo}
                                </option>
                              ))}
                            </select>
                          )}

                          <select
                            value={sortMode}
                            onChange={(event) => {
                              setSortMode(event.target.value);
                              setVisibleCount(20);
                            }}
                            aria-label={t("songFinder.search.filters.sort")}
                            className={getFilterSelectClass(sortMode !== "title")}
                          >
                            <option value="title">
                              {t("songFinder.search.filters.sortSong")}
                            </option>
                            <option value="artist">
                              {t("songFinder.search.filters.sortArtist")}
                            </option>
                          </select>
                        </div>

                        {(hasActiveFilters || sortMode !== "title") && (
                          <div className="mt-4 border-t border-white/10 pt-4">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="mr-1 text-[0.65rem] font-black uppercase tracking-[0.16em] text-zinc-500">
                                {t("songFinder.search.filters.active")}
                              </span>

                              {artistFilter && (
                                <button
                                  type="button"
                                  onClick={() => clearSingleFilter("artist")}
                                  className="inline-flex items-center gap-2 rounded-full border border-red-400/35 bg-red-500/10 px-3 py-1.5 text-xs font-black text-red-100 transition hover:border-red-400/60 hover:bg-red-500/15"
                                >
                                  <span>{selectedArtistLabel || artistFilter}</span>
                                  <span aria-hidden="true" className="text-red-300/80">×</span>
                                </button>
                              )}

                              {genreFilter && (
                                <button
                                  type="button"
                                  onClick={() => clearSingleFilter("genre")}
                                  className="inline-flex items-center gap-2 rounded-full border border-red-400/35 bg-red-500/10 px-3 py-1.5 text-xs font-black text-red-100 transition hover:border-red-400/60 hover:bg-red-500/15"
                                >
                                  <span>{genreFilter}</span>
                                  <span aria-hidden="true" className="text-red-300/80">×</span>
                                </button>
                              )}

                              {decadeFilter && (
                                <button
                                  type="button"
                                  onClick={() => clearSingleFilter("decade")}
                                  className="inline-flex items-center gap-2 rounded-full border border-red-400/35 bg-red-500/10 px-3 py-1.5 text-xs font-black text-red-100 transition hover:border-red-400/60 hover:bg-red-500/15"
                                >
                                  <span>{decadeFilter}</span>
                                  <span aria-hidden="true" className="text-red-300/80">×</span>
                                </button>
                              )}

                              {languageFilter && (
                                <button
                                  type="button"
                                  onClick={() => clearSingleFilter("language")}
                                  className="inline-flex items-center gap-2 rounded-full border border-red-400/35 bg-red-500/10 px-3 py-1.5 text-xs font-black text-red-100 transition hover:border-red-400/60 hover:bg-red-500/15"
                                >
                                  <span>{getLanguageFilterLabel(languageFilter)}</span>
                                  <span aria-hidden="true" className="text-red-300/80">×</span>
                                </button>
                              )}

                              {tempoFilter && (
                                <button
                                  type="button"
                                  onClick={() => clearSingleFilter("tempo")}
                                  className="inline-flex items-center gap-2 rounded-full border border-red-400/35 bg-red-500/10 px-3 py-1.5 text-xs font-black text-red-100 transition hover:border-red-400/60 hover:bg-red-500/15"
                                >
                                  <span>{tempoFilter}</span>
                                  <span aria-hidden="true" className="text-red-300/80">×</span>
                                </button>
                              )}

                              {sortMode === "artist" && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSortMode("title");
                                    setVisibleCount(20);
                                  }}
                                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-xs font-black text-zinc-200 transition hover:border-red-400/30 hover:text-white"
                                >
                                  <span>{t("songFinder.search.filters.sortArtist")}</span>
                                  <span aria-hidden="true" className="text-zinc-400">×</span>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            )}

            {limitMessage && (
              <div className="mt-5 rounded-[1.5rem] border border-red-500/25 bg-[#10100f] px-5 py-4 text-sm font-semibold leading-6 text-[#fecaca] shadow-[0_16px_36px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.05)]">
                {limitMessage}
              </div>
            )}

            {hasSearch && suggestedSongs.length > 0 && (
              <Reveal delay={150}>
                <div className="mt-5 hidden rounded-[1.75rem] border border-[#5a3824]/12 bg-[#fff8ef]/80 p-4 shadow-[0_14px_38px_rgba(58,43,24,0.10)] theme-dark:border-[#ef4444]/20 theme-dark:bg-[#10140f]/80 theme-dark:shadow-black/20 md:block">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                        {t("songFinder.search.suggestions")}
                      </p>

                      <h3 className="mt-1 text-base font-semibold tracking-[-0.03em] text-[#241a16] theme-dark:text-white">
                        {t("songFinder.search.suggestedMatches")}
                      </h3>
                    </div>

                    <p className="text-xs leading-5 text-[#8a5a35] theme-dark:text-zinc-500">
                      {t("songFinder.search.chooseSong")}
                    </p>
                  </div>

                  <div className="mt-3 grid gap-2 md:grid-cols-3">
                    {suggestedSongs.map((song) => (
                      <button
                        key={`suggested-${song.title}-${song.artist}`}
                        type="button"
                        onClick={() => openRequestModal(song)}
                        className="rounded-2xl border border-[#5a3824]/12 bg-white/70 px-3 py-2.5 text-left transition hover:-translate-y-0.5 hover:border-[#5a3824]/25 hover:bg-white theme-dark:border-white/10 theme-dark:bg-white/[0.025] theme-dark:hover:border-[#ef4444]/35 theme-dark:hover:bg-[#ef4444]/10"
                      >
                        <span className="block text-sm font-semibold tracking-[-0.02em] text-[#241a16] theme-dark:text-white">
                          {song.title}
                        </span>

                        <span className="mt-0.5 block text-xs font-semibold text-[#8a5a35] theme-dark:text-zinc-500">
                          {[song.artist, getSongDecade(song), song.genre]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>

                        <span className="mt-2 inline-flex rounded-full border border-[#5a3824]/20 px-2 py-0.5 text-[0.65rem] font-semibold text-[#5a3824] theme-dark:border-[#ef4444]/25 theme-dark:text-[#fecaca]">
                          {t("songFinder.search.sendRequest")}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}

            {hasSearch && (
              <div className="mt-6 hidden flex-col gap-3 rounded-[1.5rem] border border-white/10 bg-[linear-gradient(145deg,#141416,#0a0a0c)] p-4 shadow-[0_16px_36px_rgba(0,0,0,0.34),inset_0_1px_0_rgba(255,255,255,0.06)] sm:flex sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-black text-white">
                    {t("songFinder.search.results")}:{" "}
                    {filteredSongs.length}
                  </p>

                  <p className="mt-1 text-xs text-zinc-400">
                    {t("songFinder.search.term")}: “{query}”
                  </p>
                </div>

                <ParanoidiButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setQuery("");
                    setVisibleCount(20);
                  }}
                  className="!px-5 !py-2.5"
                >
                  {t("songFinder.search.clear")}
                </ParanoidiButton>
              </div>
            )}

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-zinc-500">
                {hasSearch
                  ? t("songFinder.search.resultsFor", { query: query.trim() })
                  : t("songFinder.search.repertoire")}
              </p>

              <p className="text-xs font-semibold text-zinc-500">
                {getVisibleSongCountLabel(
                  filteredSongs.length,
                  requestableSongs.length
                )}
              </p>
            </div>

            <div className="mt-5 grid gap-2 lg:grid-cols-2">
              {visibleSongs.map((song, index) => {
                const showArtistHeading =
                  sortMode === "artist" &&
                  (index === 0 ||
                    String(visibleSongs[index - 1]?.artist || "") !==
                      String(song.artist || ""));

                return (
                  <div
                    key={`${song.title}-${song.artist}`}
                    className="contents"
                  >
                    {showArtistHeading && (
                      <div className="mt-5 rounded-2xl border border-red-400/15 bg-red-500/[0.05] px-4 py-3 lg:col-span-2">
                        <div className="flex items-center gap-3">
                          <span className="h-8 w-1 rounded-full bg-red-400/80" />
                          <div>
                            <p className="text-[0.62rem] font-black uppercase tracking-[0.18em] text-red-400">
                              {t("songFinder.search.artistHeading")}
                            </p>
                            <h3 className="mt-0.5 text-xl font-black tracking-[-0.04em] text-white">
                              {song.artist || t("songFinder.search.unknownArtist")}
                            </h3>
                          </div>
                        </div>
                      </div>
                    )}

                    <Reveal
                      delay={Math.min(index * 25, 180)}
                      className="group relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-[linear-gradient(145deg,#171719_0%,#0d0d10_58%,#09090b_100%)] p-4 shadow-[0_20px_44px_rgba(0,0,0,0.42),0_7px_16px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-8px_18px_rgba(0,0,0,0.18)] transition duration-300 hover:-translate-y-1 hover:border-red-400/30 hover:shadow-[0_30px_66px_rgba(0,0,0,0.56),0_12px_26px_rgba(0,0,0,0.35),0_0_34px_rgba(239,68,68,0.10),inset_0_1px_0_rgba(255,255,255,0.10)] sm:rounded-[1.75rem]"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-black leading-tight tracking-[-0.04em] text-white">
                            {song.title}
                          </h3>

                          <p className="mt-0.5 text-sm font-semibold text-zinc-400">
                            {song.artist}
                          </p>
                        </div>

                        <span className="text-xs font-semibold tabular-nums text-white/20">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      </div>

                      <ParanoidiButton
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => openRequestModal(song)}
                        className="mt-4 !px-4 !py-2"
                      >
                        <span>{t("songFinder.search.sendRequest")}</span>
                        <span
                          aria-hidden="true"
                          className="transition group-hover:translate-x-1"
                        >
                          →
                        </span>
                      </ParanoidiButton>
                    </Reveal>
                  </div>
                );
              })}
            </div>

            {(hasMoreSongs || hasExpandedSongs) && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                {hasMoreSongs && (
                  <ParanoidiButton
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => setVisibleCount((current) => current + 20)}
                    className="!px-6 !py-3"
                  >
                    {t("songFinder.search.showMore")} ({filteredSongs.length - visibleCount})
                  </ParanoidiButton>
                )}

                {hasExpandedSongs && (
                  <ParanoidiButton
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={() => {
                      setVisibleCount(20);

                      window.setTimeout(() => {
                        resultsTopRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }, 50);
                    }}
                    className="!px-6 !py-3"
                  >
                    {t("songFinder.search.showFirstTwenty")}
                  </ParanoidiButton>
                )}
              </div>
            )}

            {!hasSearch && hasActiveFilters && filteredSongs.length === 0 && (
              <Reveal>
                <div className="mt-6 rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-6 text-center">
                  <h3 className="text-xl font-black text-white">
                    {t("songFinder.search.noFilterResultsTitle")}
                  </h3>
                  <p className="mx-auto mt-2 max-w-xl text-sm font-semibold leading-6 text-zinc-400">
                    {t("songFinder.search.noFilterResultsText")}
                  </p>
                  <ParanoidiButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={resetSongFilters}
                    className="mt-4 !px-5 !py-2.5"
                  >
                    {t("songFinder.search.filters.resetFilters")}
                  </ParanoidiButton>
                </div>
              </Reveal>
            )}

            {hasSearch && filteredSongs.length === 0 && (
            <Reveal>
              <div className="mt-6 rounded-[1.75rem] border border-zinc-200 bg-white p-8 text-center theme-dark:border-white/10 theme-dark:bg-white/[0.04]">
                <h3 className="text-2xl font-semibold text-zinc-950 theme-dark:text-white">
                  {t("songFinder.search.noResultsTitle")}
                </h3>

                <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-zinc-600 theme-dark:text-zinc-400">
                  {t("songFinder.search.noResultsText")}
                </p>

                <div className="mx-auto mt-5 max-w-2xl rounded-2xl border border-[#5a3824]/15 bg-[#fff8ef] px-4 py-4 text-left theme-dark:border-[#ef4444]/20 theme-dark:bg-black/20">
                  <p className="text-[0.68rem] font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                    {t("songFinder.search.freeRequestEyebrow")}
                  </p>

                  <p className="mt-2 text-lg font-semibold tracking-[-0.03em] text-[#241a16] theme-dark:text-white">
                    “{query.trim()}”
                  </p>
                </div>

                <ParanoidiButton
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={openFreeRequestModal}
                  className="mt-6 !px-6 !py-3"
                >
                  {t("songFinder.search.freeRequestButton")}
                </ParanoidiButton>
              </div>
            </Reveal>
            )}
          </div>

          {isRequestLimitFull && (
            <div className="absolute inset-x-4 top-6 z-20 flex justify-center sm:inset-x-0 sm:top-10 sm:mx-auto sm:max-w-2xl sm:px-4">
              <div className="w-full rounded-[1.35rem] border border-[#5a3824]/20 bg-[#fff8ef]/95 p-4 text-center shadow-2xl shadow-[#3a2b18]/20 backdrop-blur theme-dark:border-[#ef4444]/25 theme-dark:bg-[#10140f]/95 theme-dark:shadow-black/40 sm:rounded-[1.75rem] sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                  {t("songFinder.limit.eyebrow")}
                </p>

                <h3 className="mt-2 text-xl font-semibold leading-tight tracking-[-0.045em] text-[#241a16] theme-dark:text-white sm:mt-3 sm:text-3xl">
                  {t("songFinder.limit.title")}
                </h3>

                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[#5f5148] theme-dark:text-zinc-300 sm:mt-4 sm:leading-7">
                  {t("songFinder.limit.description")}
                </p>

                <p className="mt-4 text-xs font-semibold text-[#8a5a35] theme-dark:text-[#fecaca]">
                  {t("songFinder.limit.count", {
                    current: currentRequestCount,
                    max: maxRequestsPerSet,
                  })}
                </p>
              </div>
            </div>
          )}
        </div>

      {canShowRequestStats && (
        <section className="relative mt-10 overflow-hidden rounded-[2rem] border border-white/10 bg-[#0b0b0e] p-[1px] shadow-[0_30px_80px_rgba(0,0,0,0.58),0_10px_26px_rgba(0,0,0,0.38)]">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent"
          />

          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-red-600/10 blur-[75px]"
          />

          <div className="relative rounded-[calc(2rem-1px)] bg-[linear-gradient(145deg,#161619_0%,#0d0d10_55%,#08080a_100%)] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-14px_36px_rgba(0,0,0,0.26)] sm:p-8">
            <div className="text-center">
              <p className="text-xs font-black uppercase tracking-[0.28em] text-red-400">
                {t("songFinder.topRequests.eyebrow")}
              </p>

              <h3 className="mt-3 text-3xl font-black tracking-[-0.045em] text-white sm:text-4xl">
                {t("songFinder.topRequests.title")}
              </h3>

              <p className="mx-auto mt-3 max-w-2xl text-sm font-semibold leading-7 text-zinc-400">
                {t("songFinder.topRequests.description")}
              </p>
            </div>

            <div className="mt-7 grid gap-3 md:grid-cols-3">
              {!hasLoadedTopSongRequests && (
                <div className="rounded-[1.5rem] border border-white/10 bg-black/30 p-6 text-center md:col-span-3">
                  <p className="text-sm font-semibold text-zinc-400">
                    {t("songFinder.topRequests.loading")}
                  </p>
                </div>
              )}

              {hasLoadedTopSongRequests && topSongRequests.length === 0 && (
                <div className="rounded-[1.5rem] border border-white/10 bg-black/30 p-6 text-center md:col-span-3">
                  <p className="text-sm font-semibold text-zinc-400">
                    {t("songFinder.topRequests.empty")}
                  </p>
                </div>
              )}

              {topSongRequests.map((item) => {
                const totalRequests = Number(
                  item.totalRequests || item.total_requests || 1
                );

                const songTitle = item.songTitle || item.song_title || "";

                return (
                  <article
                    key={`${item.position}-${songTitle}-${item.artist || ""}`}
                    className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-[linear-gradient(145deg,#171719_0%,#0d0d10_60%,#09090b_100%)] p-5 text-center shadow-[0_18px_42px_rgba(0,0,0,0.34),inset_0_1px_0_rgba(255,255,255,0.06)] transition duration-300 hover:-translate-y-1 hover:border-red-400/30"
                  >
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute -right-12 -top-12 h-28 w-28 rounded-full bg-red-500/10 blur-3xl"
                    />

                    <div className="relative">
                      <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full border border-red-300/20 bg-red-500 text-sm font-black text-white shadow-[0_0_24px_rgba(239,68,68,0.25)]">
                        {item.position}
                      </div>

                      <h4 className="mt-4 text-xl font-black tracking-[-0.04em] text-white">
                        {songTitle}
                      </h4>

                      {item.artist && (
                        <p className="mt-1 text-sm font-semibold text-zinc-400">
                          {item.artist}
                        </p>
                      )}

                      <p className="mt-4 text-xs font-black uppercase tracking-[0.18em] text-red-200">
                        {t("songFinder.topRequests.requestCount", {
                          count: totalRequests,
                        })}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      </div>

      {toastMessage && (
        <div className="fixed left-1/2 top-5 z-[100000] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-2xl border border-red-300/30 bg-zinc-950/95 px-5 py-4 text-center text-sm font-black text-white shadow-2xl shadow-black/50 backdrop-blur">
          {toastMessage}
        </div>
      )}

      {selectedSong && createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center overflow-hidden bg-black/85 px-2 py-1 backdrop-blur-md sm:px-4 sm:py-4"
          role="dialog"
          aria-modal="true"
          aria-label={`${t("songFinder.modal.ariaRequestSong")} ${selectedSong.title}`}
        >
          <style>
            {`
              @keyframes paranoidi-skull-attack {
                0%, 100% {
                  transform: translate3d(0, 0, 0) scale(1) rotate(-3deg);
                  opacity: 0.96;
                }

                35% {
                  transform: translate3d(0, -10px, 0) scale(1.08) rotate(4deg);
                  opacity: 1;
                }

                70% {
                  transform: translate3d(0, 3px, 0) scale(0.98) rotate(-2deg);
                  opacity: 0.98;
                }
              }

              @keyframes paranoidi-glow-pulse {
                0%, 100% {
                  opacity: 0.55;
                  transform: scale(1);
                }

                50% {
                  opacity: 1;
                  transform: scale(1.08);
                }
              }

              @keyframes paranoidi-red-alert {
                0% {
                  opacity: 0;
                }

                8% {
                  opacity: 0.72;
                }

                18% {
                  opacity: 0;
                }

                28% {
                  opacity: 0.48;
                }

                42%,
                100% {
                  opacity: 0;
                }
              }

              @keyframes paranoidi-mode-stamp {
                0%,
                10% {
                  opacity: 0;
                  transform: translate(-50%, -50%) scale(2.2) rotate(-8deg);
                  filter: blur(10px);
                }

                20% {
                  opacity: 1;
                  transform: translate(-50%, -50%) scale(0.92) rotate(-4deg);
                  filter: blur(0);
                }

                28% {
                  transform: translate(-50%, -50%) scale(1.04) rotate(-4deg);
                }

               60% {
                  opacity: 1;
                  transform: translate(-50%, -50%) scale(1) rotate(-4deg);
                }

                82% {
                  opacity: 1;
                  transform: translate(-50%, -50%) scale(1.03) rotate(-4deg);
                }

                100% {
                  opacity: 0;
                  transform: translate(-50%, -50%) scale(1.12) rotate(-4deg);
                }
              }

              @keyframes paranoidi-rock-hand-rise {
                0% {
                  opacity: 0;
                  transform: translate(-50%, 120%) rotate(-12deg) scale(0.7);
                }

                18% {
                  opacity: 1;
                  transform: translate(-50%, 0) rotate(5deg) scale(1.08);
                }

                32% {
                  transform: translate(-50%, -5%) rotate(-2deg) scale(1);
                }

                72% {
                  opacity: 1;
                  transform: translate(-50%, -5%) rotate(-2deg) scale(1);
                }

                100% {
                  opacity: 0;
                  transform: translate(-50%, -35%) rotate(4deg) scale(1.12);
                }
              }
            `}
            
          </style>

          {isSent && isSelectedParanoid && (
            <>
              {/* Nopea punainen hälytysvälähdys koko ruudulle. */}
              <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-0 z-[10020] bg-red-600"
                style={{
                  animation: "paranoidi-red-alert 2.2s ease-out forwards",
                  opacity: 0,
                }}
              />

              {/* PARANOID MODE iskeytyy hetkeksi käyttäjän silmille. */}
             <div
                aria-hidden="true"
                className="pointer-events-none fixed left-1/2 top-[28%] z-[10080] whitespace-nowrap"
                style={{
                  animation:
                    "paranoidi-mode-stamp 2.6s cubic-bezier(0.2,0.9,0.25,1) forwards",
                  opacity: 0,
                }}
              >
                <div className="rotate-[-4deg] rounded-xl border-2 border-red-500 bg-black/90 px-4 py-2.5 text-center shadow-[0_0_45px_rgba(239,68,68,0.75)]">
                  <p className="text-[0.55rem] font-black uppercase tracking-[0.28em] text-red-300 sm:text-[0.65rem]">
                    ⚠ {t("songFinder.paranoid.specialActivatedShort")}
                  </p>

                  <p className="mt-1 text-2xl font-black uppercase tracking-[-0.05em] text-white sm:text-4xl">
                    {t("songFinder.paranoid.mode")}
                  </p>
                </div>
              </div>
               {/* Rock-käsi nousee hetkeksi ruudun alareunasta. */}
              <div
                aria-hidden="true"
                className="pointer-events-none fixed bottom-4 left-1/2 z-[10075] text-[7rem] drop-shadow-[0_0_35px_rgba(239,68,68,0.75)] sm:text-[9rem]"
                style={{
                  animation:
                    "paranoidi-rock-hand-rise 4.5s cubic-bezier(0.2,0.85,0.25,1) 2.6s forwards",
                  opacity: 0,
                }}
              >
                🤘
              </div>
            </>
          )}

          {isSent && isSelectedParanoid && viewportSize.width > 0 && (
            <div className="pointer-events-none fixed inset-0 z-[10000] overflow-hidden">
              <Suspense fallback={null}>
                <ReactConfetti
                  width={viewportSize.width}
                  height={viewportSize.height}
                  recycle={true}
                  numberOfPieces={180}
                  gravity={0.18}
                  tweenDuration={5000}
                  initialVelocityY={24}
                  initialVelocityX={10}
                  colors={["#ef4444", "#fecaca", "#ffffff", "#ead6c2", "#8a5a35"]}
                />
              </Suspense>
            </div>
          )}

          <div
            className={[
              "relative flex max-h-[calc(100svh-0.75rem)] w-full max-w-xl flex-col overflow-hidden rounded-[1.5rem] border shadow-2xl shadow-black/70 sm:max-h-[calc(100svh-2rem)] sm:rounded-[2rem]",
              isSent && isSelectedParanoid
                ? "border-[#5a3824]/15 bg-[#f7efe3] text-[#241a16] theme-dark:border-[#ef4444]/20 theme-dark:bg-[#050706] theme-dark:text-white"
                : "border-white/10 bg-[#f7f5f1] text-zinc-950 theme-dark:bg-zinc-950 theme-dark:text-white",
            ].join(" ")}
          >
          <div
            className={[
              "relative z-[20] shrink-0 overflow-hidden border-b px-4 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5",
              isSent && isSelectedParanoid
                ? "border-[#5a3824]/12 theme-dark:border-[#ef4444]/12"
                : "border-zinc-200 theme-dark:border-white/10",
            ].join(" ")}
          >
            {/* Keikkamainen punainen hehku headerin taustalle. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-16 -top-24 h-52 w-52 rounded-full bg-red-600/20 blur-3xl"
            />

            <div className="relative flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-red-400/25 bg-red-500/15 text-2xl text-red-300 shadow-[0_0_30px_rgba(239,68,68,0.16)] sm:h-16 sm:w-16 sm:text-3xl">
                <FaMusic aria-hidden="true" />
              </div>

              <div className="min-w-0 flex-1 pr-10">
                <p className="text-[0.65rem] font-black uppercase tracking-[0.24em] text-red-300">
                  {t("songFinder.modal.songRequest")}
                  {config.artistName ? ` · ${config.artistName}` : ""}
                </p>

                <h3 className="mt-1.5 text-3xl font-black leading-[0.95] tracking-[-0.055em] text-zinc-950 theme-dark:text-white sm:text-4xl">
                  {selectedSong.title}
                </h3>

                {selectedSong.artist && (
                  <p className="mt-2 text-sm font-bold text-zinc-500 theme-dark:text-zinc-400 sm:text-base">
                    {selectedSong.artist}
                  </p>
                )}
              </div>

              <div className="absolute right-0 top-0">
                <ParanoidiButton
                  type="button"
                  variant="icon"
                  onClick={closeRequestModal}
                  ariaLabel={
                    t("songFinder.modal.closeRequest")
                  }
                >
                  ×
                </ParanoidiButton>
              </div>
            </div>
          </div>

            {isSent ? (
              isSelectedParanoid ? (
                <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-6 pt-5">
                  <div className="pointer-events-none absolute left-1/2 top-14 z-[60] -translate-x-1/2 -translate-y-1/2 sm:top-16">
                  <div className="absolute inset-0 rounded-full bg-[#ef4444]/10 blur-3xl" />

                  <div
                    aria-hidden="true"
                    className="relative flex h-24 w-24 items-center justify-center rounded-full text-6xl drop-shadow-[0_0_28px_rgba(183,255,74,0.55)] sm:h-32 sm:w-32 sm:text-7xl"
                    style={{
                      animation:
                        "paranoidi-skull-attack 4s cubic-bezier(0.2,0.9,0.25,1) infinite",
                      transformOrigin: "50% 50%",
                      willChange: "transform, opacity",
                    }}
                  >
                    💀
                  </div>
                </div>
                  <div className="relative overflow-visible rounded-[1.75rem] bg-[linear-gradient(180deg,#fff8ef_0%,#ead6c2_100%)] px-4 pb-4 pt-16 shadow-[0_18px_55px_rgba(90,56,36,0.16)] sm:pt-20 theme-dark:bg-[linear-gradient(180deg,#020302_0%,#060806_100%)] theme-dark:shadow-[0_0_40px_rgba(183,255,74,0.16)]">
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                      <div className="absolute inset-0 animate-[paranoidi-glow-pulse_2.8s_ease-in-out_infinite] bg-[radial-gradient(circle_at_50%_24%,rgba(183,255,74,0.18),transparent_28%),radial-gradient(circle_at_50%_72%,rgba(183,255,74,0.08),transparent_34%)] theme-light:bg-[radial-gradient(circle_at_50%_24%,rgba(183,255,74,0.12),transparent_30%),radial-gradient(circle_at_50%_72%,rgba(138,90,53,0.08),transparent_34%)]" />
                    </div>

                    <div className="relative flex flex-col items-center text-center">
                      <div className="mb-5 grid w-full gap-2 text-left text-[0.72rem] font-black uppercase tracking-[0.18em] text-[#fecaca] sm:grid-cols-3">
                        <div className="rounded-2xl border border-[#ef4444]/25 bg-[#ef4444]/10 px-3 py-2 text-center animate-pulse">
                          ⚠ {t("songFinder.paranoid.mode")}
                        </div>

                        <div className="rounded-2xl border border-[#b7ff4a]/25 bg-[#b7ff4a]/10 px-3 py-2 text-center text-[#d9ff99]">
                          {t("songFinder.paranoid.received")}
                        </div>

                        <div className="rounded-2xl border border-white/15 bg-white/[0.05] px-3 py-2 text-center">
                          {t("songFinder.paranoid.rockOn")}
                        </div>
                      </div>

                        <p className="text-[0.72rem] font-black uppercase tracking-[0.28em] text-[#8a5a35] theme-dark:text-[#ef4444]">
                          {t("songFinder.paranoid.specialActivated")}
                        </p>

                        <h4 className="mt-2 text-3xl font-black uppercase leading-none tracking-[-0.055em] text-[#241a16] theme-dark:text-white sm:text-4xl">
                          {t("songFinder.paranoid.playParanoid")}
                        </h4>

                        <p className="mx-auto mt-4 max-w-md text-sm font-semibold leading-6 text-[#5f5148] theme-dark:text-zinc-300">
                          {t("songFinder.paranoid.description")}
                        </p>

                        {/* Tämä vaihtuu satunnaisesti jokaisella onnistuneella Paranoid-toiveella. */}
                        <p className="mx-auto mt-5 max-w-sm text-base font-black leading-6 text-[#241a16] theme-dark:text-white">
                          “{t(paranoidEndingKey)}”
                        </p>

                        <p className="mt-4 text-xs font-black uppercase tracking-[0.18em] text-[#8a5a35] theme-dark:text-[#fecaca]">
                          {t("songFinder.paranoid.logged")}
                        </p>
                    </div>
                  </div>

                  {renderPaidRequestBoostCard()}

                  <div className="mt-5">
                    <SponsorRotationBlock
                      sponsor={activeSponsor}
                      sponsorLabel={t("songFinder.sponsor.defaultLabel")}
                      showDescription={false}
                      paranoidStyle
                    />
                  </div>

                  <ParanoidiButton
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={closeRequestModal}
                    className="mt-6 !px-6 !py-3"
                  >
                    {t("songFinder.modal.close")}
                  </ParanoidiButton>
                </div>
              ) : (
                <div className="relative overflow-hidden p-6">
                  <div className="relative rounded-[1.5rem] border border-red-200 bg-red-50 p-5 text-red-900 theme-dark:border-red-500/20 theme-dark:bg-red-500/10 theme-dark:text-red-200">
                    <h4 className="text-2xl font-semibold">
                      {t("songFinder.modal.sentTitle")}
                    </h4>

                    <p className="mt-3 text-sm leading-7">
                      {sentNotice || t("songFinder.modal.sentText")}
                    </p>
                  </div>

                  {renderPaidRequestBoostCard()}

                  <div className="mt-5">
                    <SponsorRotationBlock
                      sponsor={activeSponsor}
                      sponsorLabel={t("songFinder.sponsor.defaultLabel")}
                      showDescription={false}
                    />
                  </div>

                  <ParanoidiButton
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={closeRequestModal}
                    className="mt-6 !px-6 !py-3"
                  >
                    {t("songFinder.modal.close")}
                  </ParanoidiButton>
                </div>
              )
            ) : (
            <form
              onSubmit={submitRequest}
              className="overflow-y-auto overscroll-contain px-3 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4"
            >
              <div className="space-y-3 sm:space-y-4">
                {publicEvents.length > 0 && (
                  <div className="rounded-[1.35rem] border border-zinc-200 bg-white/80 p-3.5 theme-dark:border-white/10 theme-dark:bg-white/[0.035] sm:p-4">
                    <label
                      htmlFor="request-event"
                      className="flex items-center gap-2 text-sm font-black text-zinc-950 theme-dark:text-white"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-500/10 text-red-500 theme-dark:text-red-300">
                        <FaCalendarAlt aria-hidden="true" />
                      </span>

                      {t("songFinder.modal.event")}
                    </label>

                    <select
                      id="request-event"
                      value={selectedEventId}
                      onChange={(event) => setSelectedEventId(event.target.value)}
                      className="mt-3 min-h-[52px] w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-bold outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-500/10 theme-dark:border-white/10 theme-dark:bg-black/30 theme-dark:text-white theme-dark:focus:border-red-400/50 sm:text-base"
                    >
                      <option value="">{t("songFinder.modal.generalEvent")}</option>

                      {publicEvents.map((eventItem) => {
                        const activeRequestCount = Number(
                          eventItem.activeRequestCount ??
                            eventItem.active_request_count ??
                            0
                        );

                        const requestLimit =
                          eventItem.requestLimit ?? eventItem.request_limit ?? null;

                        const isFull =
                          eventItem.isFull ??
                          eventItem.is_full ??
                          false;

                        const countText =
                          requestLimit !== null && requestLimit !== undefined
                            ? ` (${activeRequestCount}/${requestLimit})`
                            : "";

                        return (
                          <option
                            key={eventItem.id}
                            value={eventItem.id}
                            disabled={Boolean(isFull)}
                          >
                            {eventItem.name}
                            {isFull ? t("songFinder.modal.eventFullSuffix") : countText}
                          </option>
                        );
                      })}
                    </select>

                    <p className="mt-2 text-xs font-semibold leading-5 text-zinc-500 theme-dark:text-zinc-500">
                      {t("songFinder.modal.eventHelp")}
                    </p>
                  </div>
                )}

                <div className="rounded-[1.35rem] border border-zinc-200 bg-white/80 p-3.5 theme-dark:border-white/10 theme-dark:bg-white/[0.035] sm:p-4">
                  <label
                    htmlFor="requester-name"
                    className="flex items-center gap-2 text-sm font-black text-zinc-950 theme-dark:text-white"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-500/10 text-red-500 theme-dark:text-red-300">
                      <FaUser aria-hidden="true" />
                    </span>

                    {t("songFinder.modal.nameLabel")}
                  </label>

                  <input
                    id="requester-name"
                    type="text"
                    value={requesterName}
                    onChange={(event) => {
                      setRequesterName(event.target.value);
                      setRequestFormError("");
                    }}
                    autoComplete="off"
                    placeholder={t("songFinder.modal.namePlaceholder")}
                    className="mt-3 min-h-[54px] w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold outline-none transition placeholder:font-medium placeholder:text-zinc-400 focus:border-red-400 focus:ring-2 focus:ring-red-500/10 theme-dark:border-white/10 theme-dark:bg-black/30 theme-dark:text-white theme-dark:focus:border-red-400/50 sm:text-base"
                  />

                  {requestFormError && (
                    <p className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold leading-6 text-red-700 theme-dark:border-red-500/25 theme-dark:bg-red-500/10 theme-dark:text-red-200">
                      {requestFormError}
                    </p>
                  )}
                </div>

                <div className="rounded-[1.35rem] border border-zinc-200 bg-white/80 p-3.5 theme-dark:border-white/10 theme-dark:bg-white/[0.035] sm:p-4">
                  <label
                    htmlFor="request-message"
                    className="flex items-center gap-2 text-sm font-black text-zinc-950 theme-dark:text-white"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-500/10 text-red-500 theme-dark:text-red-300">
                      <FaCommentDots aria-hidden="true" />
                    </span>

                    {t("songFinder.modal.messageLabel")}

                    <span className="ml-auto text-[0.65rem] font-black uppercase tracking-[0.14em] text-zinc-400">
                      {t("songFinder.modal.optional")}
                    </span>
                  </label>

                  <textarea
                    id="request-message"
                    value={requestMessage}
                    onChange={(event) => setRequestMessage(event.target.value)}
                    placeholder={t("songFinder.modal.messagePlaceholder")}
                    rows={2}
                    className="mt-3 w-full resize-none rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold outline-none transition placeholder:font-medium placeholder:text-zinc-400 focus:border-red-400 focus:ring-2 focus:ring-red-500/10 theme-dark:border-white/10 theme-dark:bg-black/30 theme-dark:text-white theme-dark:focus:border-red-400/50 sm:text-base"
                  />
                </div>

                {isRequestRaffleEnabled && (
                  <label className="flex cursor-pointer items-start gap-3 rounded-[1.35rem] border border-red-300/20 bg-red-500/[0.055] px-4 py-3.5 text-left transition hover:bg-red-500/[0.08]">
                    <input
                      type="checkbox"
                      checked={wantsRaffle}
                      onChange={(event) => setWantsRaffle(event.target.checked)}
                      className="mt-1 h-5 w-5 shrink-0 accent-red-600"
                    />

                    <span>
                      <span className="block text-sm font-black text-zinc-950 theme-dark:text-white">
                        {t("songFinder.modal.raffleLabel")}
                      </span>

                      <span className="mt-1 block text-xs font-semibold leading-5 text-zinc-500 theme-dark:text-zinc-400">
                        {t("songFinder.modal.raffleHelp")}
                      </span>
                    </span>
                  </label>
                )}

                {activeSponsor && (
                  <div className="rounded-[1.35rem] border border-white/10 bg-black/[0.025] px-2 py-1 theme-dark:bg-black/20">
                    <SponsorRotationBlock
                      sponsor={activeSponsor}
                      sponsorLabel={t("songFinder.sponsor.defaultLabel")}
                      sponsorDescription={getSponsorDescription(activeSponsor)}
                      inline
                    />
                  </div>
                )}

                <div className="pt-1">
                  <ParanoidiButton
                    type="submit"
                    variant="primary"
                    size="md"
                    disabled={isSubmittingRequest}
                    className="group w-full !rounded-2xl !px-5 !py-4 text-center text-base font-black shadow-[0_12px_36px_rgba(220,38,38,0.25)] disabled:pointer-events-none disabled:opacity-60"
                  >
                    <span className="flex w-full items-center justify-center gap-2">
                      <FaPaperPlane
                        aria-hidden="true"
                        className="text-sm transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />

                      {isSubmittingRequest
                        ? t("songFinder.modal.sending")
                        : t("songFinder.search.sendRequest")}
                    </span>
                  </ParanoidiButton>

                  <button
                    type="button"
                    onClick={closeRequestModal}
                    className="mt-2.5 w-full rounded-xl px-4 py-2.5 text-sm font-bold text-zinc-500 transition hover:bg-black/5 hover:text-zinc-800 theme-dark:text-zinc-500 theme-dark:hover:bg-white/[0.04] theme-dark:hover:text-zinc-300"
                  >
                    {t("songFinder.modal.cancel")}
                  </button>
                </div>
              </div>
            </form>
            )}
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}